import React, { useRef, useEffect, useState } from 'react';
import Icon from '../shared/Icon';
import './DrilldownChartPanel.css';
import { useFlag } from '@unleash/proxy-client-react';
import { getRenderer } from '../Insights/renderers';
import { WIDGET_TEXT_EDIT_FLAG } from '../Insights/WidgetTextEditModal';

// PRD-1337: the chart the viewer drilled from, pinned above the drill-down
// conversation so the ResAI framing turn sits next to the actual chart (not just a
// text summary of it). Fed by the widget conversation's frozen `chart` snapshot, so
// it shows the exact frozen slice the conversation is bound to — and survives a
// refresh, since the snapshot is re-fetched from the binding by thread id.
//
// Presentational only: it measures its own container (the renderers are sized, not
// responsive) and draws the frozen result. No querying, no refresh — that is the
// widget's job on the Insights page, never here.
function DrilldownChartPanel({ chart }) {
  const textEditEnabled = useFlag(WIDGET_TEXT_EDIT_FLAG);
  const chartRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!chartRef.current) return undefined;
    const observer = new ResizeObserver(() => {
      if (!chartRef.current) return;
      const rect = chartRef.current.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  if (!chart) return null;
  const chartData = chart.data;
  // PRD-1467: a table widget's frozen result is the tabular shape ({ columns, rows }),
  // which has neither `data` nor `value` and isn't a bare array — so the pinned panel
  // read it as empty and showed "Chart unavailable" for every table drill-down. Treat
  // `rows` as present-data too (the table renderer draws from { columns, rows }).
  const hasData = !!(
    chartData &&
    (chartData.data || chartData.rows || chartData.value !== undefined || Array.isArray(chartData))
  );
  const Renderer = getRenderer(chart.widget_type_id);

  return (
    <div className="dcp-panel">
      <div className="dcp-header">
        <Icon name="insights" className="dcp-header-icon" size={16} />
        <span className="dcp-title">{chart.title || 'Widget'}</span>
      </div>
      <div className="dcp-chart" ref={chartRef}>
        {hasData && Renderer && size.width > 0 ? (
          <Renderer
            data={chartData?.data || chartData}
            width={size.width}
            height={size.height}
            xLabel={chartData?.x_label || chart.x_label}
            yLabel={chartData?.y_label || chart.y_label}
            displayLabel={chart.display_label}
            labelMode={textEditEnabled}
          />
        ) : (
          <div className="dcp-empty">Chart unavailable.</div>
        )}
      </div>
    </div>
  );
}

export default DrilldownChartPanel;
